import { memo, useCallback, useRef } from 'react'

import { RemoveButton } from 'core-components/library/multiSelect'
import { AbsolutePopup, IPopupMethods, PopPaper } from 'core-components/library/popup'
import { getFormatDate } from 'core/classes/helpers/DateHelper'

import { getMultiSelectFilterValue, getSelectOptionLabel } from '../../lib/filterValue'
import { FilterConfig, FilterValue, RangeValue, SelectOption } from '../../types'
import { FilterValueEditor } from '../filterValueEditor/FilterValueEditor'

import styles from './FilterChip.module.scss'

interface FilterChipProps {
  config: FilterConfig
  value: FilterValue
  onChange: (value: FilterValue) => void
  onRemove: () => void
}

const formatDefaultValue = (
  value: FilterValue,
  type: FilterConfig['type'],
  options?: SelectOption[]
): string => {
  if (value === null || value === undefined) {
    return 'Any'
  }

  switch (type) {
    case 'text':
      return String(value)

    case 'select':
      if (options) {
        return getSelectOptionLabel(value as string | number, options)
      }
      return String(value)

    case 'multiSelect': {
      const values = getMultiSelectFilterValue(value)

      if (!values.length) {
        return 'Any'
      }

      return values.map((item) => getSelectOptionLabel(item, options)).join(', ')
    }

    case 'date':
      if (value instanceof Date) {
        return getFormatDate(value, 'custom', 'YYYY-MM-DD') || 'Any'
      }
      return String(value)

    case 'dateRange': {
      const rangeVal = value as { from: Date | null; to: Date | null }
      const formatDate = (date: Date) => {
        return getFormatDate(date, 'custom', 'YYYY-MM-DD')
      }
      const from = rangeVal.from instanceof Date ? formatDate(rangeVal.from) : ''
      const to = rangeVal.to instanceof Date ? formatDate(rangeVal.to) : ''
      if (!from && !to) return 'Any'
      if (!from) return `≤ ${to}`
      if (!to) return `≥ ${from}`
      return `${from} – ${to}`
    }

    case 'range': {
      const rangeVal = value as RangeValue
      const from = rangeVal.from !== null ? rangeVal.from : ''
      const to = rangeVal.to !== null ? rangeVal.to : ''
      if (!from && !to) return 'Any'
      if (!from) return `≤ ${to}`
      if (!to) return `≥ ${from}`
      return `${from} – ${to}`
    }

    default:
      return String(value)
  }
}

export const FilterChip = memo<FilterChipProps>(({ config, value, onChange, onRemove }) => {
  const popupRef = useRef<IPopupMethods>(null)

  const displayValue = config.formatValue
    ? config.formatValue(value)
    : formatDefaultValue(value, config.type, config.options)

  const handleApply = useCallback(
    (newValue: FilterValue) => {
      onChange(newValue)
      popupRef.current?.close()
    },
    [onChange]
  )

  const handleRemoveClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onRemove()
    },
    [onRemove]
  )

  return (
    <AbsolutePopup
      ref={popupRef}
      rootClassName={styles.root}
      popupClassName={styles.popup}
      popupContent={({ action }) => (
        <PopPaper className={styles.popPaper}>
          <FilterValueEditor
            config={config}
            value={value}
            onApply={handleApply}
            onCancel={action.close}
          />
        </PopPaper>
      )}
    >
      <div className={styles.chip}>
        <span className={styles.label}>{config.label}:</span>
        <span className={styles.value}>{displayValue}</span>
        <div className={styles.removeWrapper} onClick={handleRemoveClick}>
          <RemoveButton onClick={onRemove} color="gray" />
        </div>
      </div>
    </AbsolutePopup>
  )
})

FilterChip.displayName = 'FilterChip'
